"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  Download,
  RefreshCw,
  Clock,
  ShieldCheck,
  Wallet,
  CheckCircle,
  XCircle,
  ChevronDown,
  Banknote,
  Users,
  Gift,
  DollarSign,
} from "lucide-react"

// Assuming these are your existing imports
import { exchangeActions, withdrawalActions } from "../../../../../redux/actions"
import {
  userSelect,
  homeScreenStatSelect,
  allRemoteSettingsSelect,
  allWithdrawalSelect,
  exchangeSelect,
} from "../../../../../redux/selectors/index"
import { notify, money, moneyStatus, formatDateTime, capitalizeFirstLetter } from "../../../../../utility/Helper1"
import LoadingIcon from "../../../../../utility/customComponents/loader/LoadingIcon"
import PinAuth from "../../../../../utility/customComponents/PinAuth"
import NoData from "../../../../../utility/customComponents/NoData"
import WrapUpP2p from "../../../p2p/layouts/WrapUpP2p"

const WalletPage = () => {
  const finance = useSelector(homeScreenStatSelect)
  const userData = useSelector(userSelect)
  const site_settings = useSelector(allRemoteSettingsSelect)
  const dispatch = useDispatch()

  const [securityObj, setSecurityObj] = useState({})
  const [currencySelected, setCurrencySelected] = useState(finance?.[0])
  const [amount, setAmount] = useState("")
  const [withdrawalIsActive, setWithdrawalIsActive] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState(1)
  const [activeTab, setActiveTab] = useState("withdraw") // 'withdraw' or 'history'
  const exchangeData = useSelector(exchangeSelect);
  const withdrawalHistory = exchangeData?.filter(el => Number(el.asset) == 2)

  const op_se = site_settings.operational_settings

  const walletSources = [
    { title: "Main Balance", column: "balance", id: 1, useThis: true, icon: <Wallet className="h-5 w-5" /> },
    {
      title: "Commission",
      column: "commission",
      id: 2,
      useThis: Number(op_se?.useReferralBonus) == 1,
      icon: <Users className="h-5 w-5" />,
    },
    // {
    //   title: "Rebate Commission",
    //   column: "rebateCommission",
    //   id: 2,
    //   balanceKey: "rebateCommission",
    //   useThis: Number(op_se?.useRebateBonus) === 1,
    //   icon: <DollarSign className="h-5 w-5" />,
    // },
    // {
    //   title: "Deposit Bonus",
    //   column: "depositBonus",
    //   id: 3,
    //   balanceKey: "depositBonus",
    //   useThis: Number(op_se?.useDepositBonus) === 1,
    //   icon: <Gift className="h-5 w-5" />,
    // },
  ]

  const processWithdrawal = (e) => {
    e.preventDefault()

    if (withdrawalIsActive) return

    if (isNaN(amount) || amount == 0) {
      return false
    }

    setWithdrawalIsActive(true)

    const walletChosenColumn = walletSources?.[selectedWallet - 1]?.["id"]

    dispatch(
      exchangeActions.onPostExchange(
        {
          amount: amount,
          ...(securityObj ?? {}),
          currency: currencySelected?.type,
        },
        `p2p/withdrawal/new/${walletChosenColumn}`,
        (response) => {
          notify(response.msg, response.code)
          if (response.code == 1) {
            setAmount("")
          }
          setWithdrawalIsActive(false)
        },
      ),
    )
  }

  const updateCurrencySelected = (typeOfSelected) => {
    const selected = finance?.filter((ite, inde) => ite.type == typeOfSelected)?.[0] ?? []
    setCurrencySelected(selected)
  }

  const changeSelectedWallet = (value) => {
    setSelectedWallet(value)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 1:
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 2:
        return <XCircle className="h-5 w-5 text-red-500" />
      case 0:
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 1:
        return "bg-green-100 text-green-800"
      case 2:
        return "bg-red-100 text-red-800"
      case 0:
      default:
        return "bg-yellow-100 text-yellow-800"
    }
  }

  return (
    <div pageTitle="Wallet">
      <div className="max-w-7xl mx-auto">
        {/* Balance Cards */}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="flex border-b border-gray-100">
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${activeTab === "withdraw" ? "text-green-600 border-b-2 border-green-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("withdraw")}
            >
              <div className="flex items-center justify-center">
                <Download className="w-5 h-5 mr-2" />
                Withdraw Funds
              </div>
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${activeTab === "history" ? "text-green-600 border-b-2 border-green-600" : "text-gray-500 hover:text-gray-700"}`}
              onClick={() => setActiveTab("history")}
            >
              <div className="flex items-center justify-center">
                <Clock className="w-5 h-5 mr-2" />
                Withdrawal History
              </div>
            </button>
          </div>

          {/* Withdraw Form */}
          {activeTab === "withdraw" && (
            <div className="p-6">
              {/* <div className="bg-green-50 rounded-lg p-4 mb-6 border border-green-100">
                <h4 className="font-medium text-green-800 mb-1">
                  {currencySelected?.withdrawal_note_headline || "Withdrawal Information"}
                </h4>
                <p className="text-green-700 text-sm">
                  {currencySelected?.withdrawal_note_body ||
                    "Please ensure your wallet address is correct before proceeding with the withdrawal."}
                </p>
              </div> */}

              <form onSubmit={processWithdrawal} className="space-y-6">
                {finance?.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Currency</label>
                    <div className="relative">
                      <select
                        onChange={(e) => updateCurrencySelected(e.target.value)}
                        className="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-lg appearance-none bg-white border"
                      >
                        {finance?.map((item, index) => (
                          <option key={index} value={item.type}>
                            {item?.abbr} - {item?.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <ChevronDown className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source Wallet</label>
                  <div className="relative">
                    <select
                      value={selectedWallet}
                      onChange={(e) => changeSelectedWallet(e.target.value)}
                      className="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-lg appearance-none bg-white border"
                    >
                      {walletSources?.map((item, index) => {
                        if (!item.useThis) return null

                        const showBalance =
                          finance?.filter((finItem) => finItem?.type == currencySelected?.type)?.[0]?.[
                            item?.balanceKey
                          ] ?? ""

                        return (
                          <option key={index} value={item?.id}>
                            {item?.title} {showBalance !== "" ? ` - ${money(showBalance, currencySelected?.type)}` : ""}
                          </option>
                        )
                      })}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount to Withdraw</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {/* <span className="text-gray-500 sm:text-sm">{currencySelected?.symbol || "$"}</span> */}
                    </div>
                    <input
                      type="text"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="focus:ring-green-500 focus:border-green-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md py-3"
                      placeholder="0.00"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">{currencySelected?.abbr}</span>
                    </div>
                  </div>
                </div>

                {/* PIN Authentication Component */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center mb-3">
                    <ShieldCheck className="h-5 w-5 text-gray-600 mr-2" />
                    <h4 className="font-medium text-gray-700">Security Verification</h4>
                  </div>
                  <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />
                </div>

                <button
                  type="submit"
                  disabled={withdrawalIsActive}
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${withdrawalIsActive ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {withdrawalIsActive ? (
                    <>
                      <span>Processing</span>
                      <LoadingIcon color={"#fff"} active={withdrawalIsActive} />
                    </>
                  ) : (
                    "Request Withdrawal"
                  )}
                </button>
              </form>
            </div>
          )}

          {/* History Table */}
          {activeTab === "history" && (
            <div className="p-6">
              {withdrawalHistory?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Source
                        </th>
                        
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Amount
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {withdrawalHistory.map((item, index) => {
                        const image = finance?.filter((filitem) => filitem.type === item.currency)[0]

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="fle items-center">
                                <div className="text-sm text-gray-900">{capitalizeFirstLetter(item?.withdrawalColumn)}</div>
                                <div className="text-sm text-gray-500">{formatDateTime(Number(item.ghtimestamp2))}</div>
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                              {money(item?.lastgh, item?.motherType)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(item?.completed)}`}
                              >
                                {getStatusIcon(item?.status)}
                                <span className="ml-1">{moneyStatus()[item?.completed]}</span>
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-10">
                  <NoData title="No history available" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WalletPage
